import type { Amount } from '@hero/schema';
import { roundPrice } from '../price/round.price';

/**
 * Apply `roundPrice` to every numeric `Amount` field that
 * `AmountService.roundAll` rounds. Pure — does not mutate the input.
 *
 * **Deliberately NOT rounded** (matching the source behaviour):
 *   - `paid` — the caller controls payment precision externally.
 *   - `fees.applied[i].amount` and the fee rollups (`all`, `added`,
 *     `subtracted`, `realized`, `total`). The source method only
 *     rounds tax + subtotal + total + remaining + discount; fees stay
 *     at whatever precision the caller supplied.
 *
 * Each per-tax `amount` IS rounded individually, in addition to
 * `tax.total`.
 */
export function roundAmount(amount: Amount): Amount {
  return {
    ...amount,
    total: roundPrice(amount.total),
    discount: {
      ...amount.discount,
      amount: roundPrice(amount.discount.amount),
    },
    subtotal: roundPrice(amount.subtotal),
    itemsOriginalSubtotal: roundPrice(amount.itemsOriginalSubtotal),
    itemsSubtotal: roundPrice(amount.itemsSubtotal),
    realizedSubtotal: roundPrice(amount.realizedSubtotal),
    remaining: roundPrice(amount.remaining),
    tax: {
      total: roundPrice(amount.tax.total),
      applied: amount.tax.applied.map(info => ({
        ...info,
        amount: roundPrice(info.amount),
      })),
    },
  };
}
